import { useSelector } from "react-redux";
import React, { useContext, useMemo } from "react";
import PieChart from "common/Charts/PieChart/PieChart";
import { DISPLAY_CURRENCIES } from "common/constants";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  TRADES_DATA_ALLOCATED_AMOUNT_BTC_HEADER,
  TRADES_DATA_ALLOCATED_AMOUNT_USD_HEADER,
} from "@shared/features/trades/common/constants";
import { LanguageNameSpaces, PrecisionType } from "@shared/types";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import { useBottomNavigator } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import { preprocessPieChartData } from "@shared/utils/charts";
import NewLookAllocation from "features/trades/NewLook/NewLookAllocation.tsx";

interface TradesPieChartProps {
  /** New look only: an action shown next to the chart title. */
  headerAction?: React.ReactNode;
  /** New look: just the donut, for the overview panel. */
  bare?: boolean;
}

const TradesPieChart = ({ headerAction, bare }: TradesPieChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const {
    filteredOpenTradesData,
    isFetching,
    isLoading,
    precisionConfig,
    pieChartData,
  } = useContext(TradesPageContext)!;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook, colors } = useTheme();

  // New look: one slice per pair. The shared data has a slice per trade, so
  // several trades on one pair would repeat its name in the short legend.
  const newLookPieChartData = useMemo(() => {
    if (!isNewLook) return pieChartData;
    const amountBySymbol = new Map<string, number>();
    filteredOpenTradesData?.forEach(({ symbol, allocated_amount_usd }) =>
      amountBySymbol.set(
        symbol,
        (amountBySymbol.get(symbol) ?? 0) + allocated_amount_usd
      )
    );
    return preprocessPieChartData(
      [...amountBySymbol].map(([name, value]) => ({ name, value })),
      colors.pieChartColors
    );
  }, [isNewLook, pieChartData, filteredOpenTradesData, colors]);

  const headerName =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? TRADES_DATA_ALLOCATED_AMOUNT_BTC_HEADER
      : TRADES_DATA_ALLOCATED_AMOUNT_USD_HEADER;
  const total = filteredOpenTradesData?.reduce((total, coinData) => {
    total += coinData[headerName];
    return total;
  }, 0);

  const totalText = useMemo(
    () => (
      <TextWithPrecisionAndIcon
        precisionConfig={precisionConfig}
        precisionType={PrecisionType.BALANCE}
        text={total}
        round={true}
      />
    ),
    [precisionConfig, total]
  );

  if (isNewLook) {
    return (
      <NewLookAllocation
        data={newLookPieChartData}
        loading={isBottomNavigator ? isLoading : isFetching}
        total={totalText}
        tooltipElementId="trades-chart-tooltip"
        action={headerAction}
        compact={isBottomNavigator}
        bare={bare}
      />
    );
  }

  return (
    <PieChart
      data={pieChartData}
      loading={isBottomNavigator ? isLoading : isFetching}
      chartName={t("charts.amount_allocation")}
      total={totalText}
      isMobileMainPageChart={isBottomNavigator}
      tooltipElementId="trades-chart-tooltip"
    />
  );
};

export default React.memo(TradesPieChart);
